import React, { useEffect, useState } from "react";
import { req } from "./api";
import { DOC_PROMPTS, DOC_COUNTRIES, promptForCountry, INAPP_FORMAT, CLAUDE_URL } from "./docPrompts";

const MODELS = [
  { id: "claude", label: "Claude (Sonnet 5.5)" },
  { id: "openai", label: "ChatGPT (GPT-5.6)" },
  { id: "gemini", label: "Gemini (3.1 Pro)" },
];
const COUNTRY_LABEL = { bd: "Bangladesh", in: "India", sg: "Singapore" };

export default function DocStudio() {
  const [open, setOpen] = useState(null);
  const [copied, setCopied] = useState(null);
  const [country, setCountry] = useState("bd");
  const [model, setModel] = useState("claude");
  const [genOpen, setGenOpen] = useState(null);
  const [inputs, setInputs] = useState({});
  const [gen, setGen] = useState({});
  const [history, setHistory] = useState([]);
  const [histDoc, setHistDoc] = useState(null);

  const loadHistory = () => req("/admin/ai/history").then(setHistory).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const copy = async (id, text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(id);
    setTimeout(() => setCopied(null), 2500);
  };

  const setG = (id, patch) => setGen((g) => ({ ...g, [id]: { ...(g[id] || {}), ...patch } }));

  const generate = async (d) => {
    const details = (inputs[d.id] || "").trim();
    const prompt = promptForCountry(d.prompt, country) +
      (details
        ? "\n\nCLIENT DETAILS AND SOURCE MATERIAL (supplied by the Rivera team):\n" + details
        : "\n\nNo client materials are attached to this run — draft the complete document with clearly marked 【placeholders】 for every client-specific fact, and list them in the Open items annex.") +
      "\n\n" + INAPP_FORMAT;
    setG(d.id, { busy: true, text: "", err: "", savedId: null });
    let reader = null;
    let lastActivity = Date.now();
    const watchdog = setInterval(() => {
      if (Date.now() - lastActivity > 120000) {
        clearInterval(watchdog);
        try { reader && reader.cancel(); } catch (e) {}
        setG(d.id, { busy: false, err: "The model stopped responding for two minutes — generation was cancelled. Please try again." });
      }
    }, 10000);
    try {
      const res = await fetch("/api/admin/ai/generate", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt, model, docId: d.id, title: d.title, country }),
      });
      if (!res.ok || !res.body) throw new Error("Request failed (" + res.status + ")");
      reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        lastActivity = Date.now();
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          let ev;
          try { ev = JSON.parse(line.slice(5)); } catch (e) { continue; }
          if (ev.t) setGen((g) => ({ ...g, [d.id]: { ...(g[d.id] || {}), text: ((g[d.id] || {}).text || "") + ev.t } }));
          if (ev.done) { setG(d.id, { busy: false, savedId: ev.id }); loadHistory(); }
          if (ev.error) setG(d.id, { busy: false, err: ev.error });
        }
      }
      setG(d.id, { busy: false });
    } catch (e) {
      setG(d.id, { busy: false, err: e.message });
    } finally {
      clearInterval(watchdog);
    }
  };

  const viewDoc = async (id) => {
    if (histDoc && histDoc.id === id) { setHistDoc(null); return; }
    try {
      setHistDoc(await req("/admin/ai/docs/" + id));
    } catch (e) {}
  };

  return (
    <div data-testid="doc-studio">
      <div className="panel" style={{ marginBottom: 26, display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
        <div className="stack" style={{ gap: 8, maxWidth: 720 }}>
          <span className="eyebrow">How to use</span>
          <p style={{ margin: 0, fontSize: 15, color: "var(--fg)" }}>
            Pick a country and an AI model, open <strong>Generate with AI</strong> on a document type, paste the client's
            details and click Generate — the document is written here, saved to the history below, and downloadable as a
            branded Word file. Or use <strong>Copy prompt</strong> and paste it into Claude yourself, with the client's
            documents attached. Bangladesh documents come English first with a full Bangla version; India and Singapore
            documents are English-only.
          </p>
        </div>
        <a className="btn" href={CLAUDE_URL} target="_blank" rel="noopener noreferrer" data-testid="open-claude-btn">
          Open Claude ↗
        </a>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12, alignItems: "center" }} data-testid="doc-country-switch">
        <span className="eyebrow" style={{ marginRight: 6 }}>Country</span>
        {DOC_COUNTRIES.map((c) => (
          <button key={c.id} className={`btn sm${country === c.id ? "" : " ghost"}`} onClick={() => setCountry(c.id)} data-testid={`doc-country-${c.id}`}>
            {c.label}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20, alignItems: "center" }} data-testid="doc-model-switch">
        <span className="eyebrow" style={{ marginRight: 6 }}>AI model</span>
        {MODELS.map((m) => (
          <button key={m.id} className={`btn sm${model === m.id ? "" : " ghost"}`} onClick={() => setModel(m.id)} data-testid={`doc-model-${m.id}`}>
            {m.label}
          </button>
        ))}
      </div>

      <div className="stack" style={{ gap: 16 }}>
        {DOC_PROMPTS.map((d) => {
          const g = gen[d.id] || {};
          return (
            <div key={d.id} className="panel" style={{ display: "flex", flexDirection: "column", gap: 12 }} data-testid={`doc-prompt-${d.id}`}>
              <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <div className="stack" style={{ gap: 4, minWidth: 0 }}>
                  <strong style={{ fontFamily: "var(--f-display)", fontSize: 19, color: "var(--ink)", fontWeight: 500 }}>{d.title}</strong>
                  <span className="muted small">{promptForCountry(d.use, country)}</span>
                </div>
                <div className="row-actions">
                  <button className="btn sm" onClick={() => setGenOpen(genOpen === d.id ? null : d.id)} data-testid={`gen-toggle-${d.id}`}>
                    {genOpen === d.id ? "Close generator" : "Generate with AI"}
                  </button>
                  <button className="btn sm ghost" onClick={() => copy(d.id, promptForCountry(d.prompt, country))} data-testid={`copy-${d.id}`}>
                    {copied === d.id ? "Copied ✓" : `Copy prompt${country !== "bd" ? ` — ${DOC_COUNTRIES.find((c) => c.id === country).label}` : ""}`}
                  </button>
                  <a className="btn sm ghost" href={CLAUDE_URL} target="_blank" rel="noopener noreferrer" data-testid={`claude-${d.id}`}>Open in Claude ↗</a>
                  <button className="btn sm ghost" onClick={() => setOpen(open === d.id ? null : d.id)} data-testid={`preview-${d.id}`}>
                    {open === d.id ? "Hide prompt" : "View prompt"}
                  </button>
                </div>
              </div>
              {open === d.id && (
                <textarea readOnly rows={16} value={promptForCountry(d.prompt, country)} style={{ fontFamily: "monospace", fontSize: 12.5, lineHeight: 1.55, whiteSpace: "pre-wrap" }}
                  onFocus={(e) => e.target.select()} data-testid={`prompt-text-${d.id}`} />
              )}
              {genOpen === d.id && (
                <div className="stack" style={{ gap: 12, borderTop: "1px solid var(--rule)", paddingTop: 14 }} data-testid={`gen-panel-${d.id}`}>
                  <label className="muted small" htmlFor={`gen-details-${d.id}`}>
                    Client details and source material (paste financials, registration details, notes — optional but better with):
                  </label>
                  <textarea id={`gen-details-${d.id}`} rows={5} value={inputs[d.id] || ""} data-testid={`gen-details-${d.id}`}
                    onChange={(e) => setInputs((s) => ({ ...s, [d.id]: e.target.value }))}
                    placeholder="e.g. Company: …, incorporated …, turnover FY2024: BDT …, facility sought: term loan BDT … for …"
                    style={{ fontFamily: "inherit", fontSize: 14 }} />
                  <div className="row-actions">
                    <button className="btn sm" disabled={g.busy} onClick={() => generate(d)} data-testid={`gen-run-${d.id}`}>
                      {g.busy ? "Generating…" : `Generate — ${MODELS.find((m) => m.id === model).label} · ${COUNTRY_LABEL[country]}`}
                    </button>
                    {g.savedId && <a className="btn sm" href={`/api/admin/ai/docs/${g.savedId}/docx`} data-testid={`gen-docx-${d.id}`}>Download Word ↓</a>}
                    {g.savedId && <button className="btn sm ghost" onClick={() => copy("out-" + d.id, g.text)} data-testid={`gen-copy-${d.id}`}>{copied === "out-" + d.id ? "Copied ✓" : "Copy text"}</button>}
                    {g.savedId && <span className="muted small">Saved to history below.</span>}
                  </div>
                  {g.err && <div className="pill" style={{ background: "#FBEDEA", borderColor: "#D9A79B", color: "#8C3B2E" }} data-testid={`gen-error-${d.id}`}>Generation failed: {g.err}</div>}
                  {(g.text || g.busy) && (
                    <div data-testid={`gen-out-${d.id}`} style={{ whiteSpace: "pre-wrap", fontSize: 13.5, lineHeight: 1.6, background: "var(--paper, #F6F3EC)", border: "1px solid var(--rule)", padding: 16, maxHeight: 420, overflowY: "auto" }}>
                      {g.text}{g.busy ? " ▌" : ""}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="panel" style={{ marginTop: 30 }} data-testid="ai-history">
        <div className="stack" style={{ gap: 4, marginBottom: 14 }}>
          <span className="eyebrow">Generated documents</span>
          <span className="muted small">Every AI run is saved here — reopen the text or download the branded Word file again.</span>
        </div>
        {history.length === 0 && <div className="muted">Nothing generated yet.</div>}
        <div className="stack" style={{ gap: 10 }}>
          {history.map((h) => (
            <div key={h.id} style={{ borderTop: "1px solid var(--rule)", paddingTop: 10 }}>
              <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                <div className="stack" style={{ gap: 2, minWidth: 0 }}>
                  <strong style={{ fontSize: 15, color: "var(--ink)" }}>{h.title}</strong>
                  <span className="muted small">
                    {(COUNTRY_LABEL[h.country] || h.country)} · {(MODELS.find((m) => m.id === h.model) || {}).label || h.model} · {(h.createdAt || "").replace("T", " ").slice(0, 16)} · by {h.user}
                  </span>
                </div>
                <div className="row-actions">
                  <button className="btn sm ghost" onClick={() => viewDoc(h.id)} data-testid={`ai-view-${h.id}`}>{histDoc && histDoc.id === h.id ? "Hide" : "View"}</button>
                  <a className="btn sm ghost" href={`/api/admin/ai/docs/${h.id}/docx`} data-testid={`ai-docx-${h.id}`}>Word ↓</a>
                </div>
              </div>
              {histDoc && histDoc.id === h.id && (
                <div className="stack" style={{ gap: 10, marginTop: 10 }}>
                  <div className="row-actions">
                    <button className="btn sm" onClick={() => copy("hist-" + h.id, histDoc.output)} data-testid={`ai-copy-${h.id}`}>{copied === "hist-" + h.id ? "Copied ✓" : "Copy text"}</button>
                  </div>
                  <div data-testid={`ai-out-${h.id}`} style={{ whiteSpace: "pre-wrap", fontSize: 13, lineHeight: 1.6, background: "var(--paper, #F6F3EC)", border: "1px solid var(--rule)", padding: 16, maxHeight: 420, overflowY: "auto" }}>{histDoc.output}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <p className="note" style={{ marginTop: 22 }}>
        Every prompt already carries the brand sheet (navy #0F1E33, brass #A8834A, ivory #F6F3EC, the wave-mark logo)
        and the selected country's practice block — Bangladesh (RJSC, Bangladesh Bank, NBR, BIDA/BEZA, BSEC, FY July–June),
        India (MCA, RBI, PAN/TAN, GST, Udyam, DPIIT, SEBI, Ind AS, FY April–March) or Singapore (ACRA, IRAS, Enterprise
        Singapore, MAS, CPF, SFRS, company FYE). In-app generation uses the workspace AI balance; the copy-paste prompts
        cost nothing here — nothing leaves this app until you click Generate or paste into Claude yourself.
      </p>
    </div>
  );
}
